/* ═══════════════════════════════════════════════════════════════
   timmyT — main.js
   - Three.js particle network hero
   - GSAP + ScrollTrigger animations
   - Counter animations
   - Mobile menu
   - AOS init
   - Contact form handler
════════════════════════════════════════════════════════════════ */

'use strict';

// ── Wait for DOM ─────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  initAOS();
  initNavbar();
  initMobileMenu();
  initHeroCanvas();
  initCounters();
  initContactForm();
  initGSAP();
});


/* ══════════════════════════════════════════════════════════════
   AOS INIT
══════════════════════════════════════════════════════════════ */
function initAOS() {
  if (typeof AOS !== 'undefined') {
    AOS.init({
      duration: 700,
      easing: 'ease-out-cubic',
      once: true,
      offset: 60,
    });
  }
}


/* ══════════════════════════════════════════════════════════════
   NAVBAR — transparent → blur on scroll
══════════════════════════════════════════════════════════════ */
function initNavbar() {
  const navbar = document.getElementById('navbar');
  if (!navbar) return;

  let ticking = false;

  const handleScroll = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        if (window.scrollY > 60) {
          navbar.classList.add('scrolled');
        } else {
          navbar.classList.remove('scrolled');
        }
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll(); // run once on load
}


/* ══════════════════════════════════════════════════════════════
   MOBILE MENU
══════════════════════════════════════════════════════════════ */
function initMobileMenu() {
  const hamburger   = document.getElementById('hamburger');
  const mobileMenu  = document.getElementById('mobileMenu');
  const mobileLinks = document.querySelectorAll('.mobile-link, .mobile-cta');

  if (!hamburger || !mobileMenu) return;

  const toggle = (force) => {
    const isOpen = typeof force === 'boolean' ? force : !mobileMenu.classList.contains('open');
    mobileMenu.classList.toggle('open', isOpen);
    hamburger.classList.toggle('active', isOpen);
    document.body.style.overflow = isOpen ? 'hidden' : '';
  };

  hamburger.addEventListener('click', () => toggle());

  mobileLinks.forEach(link => {
    link.addEventListener('click', () => toggle(false));
  });

  // Close on overlay click
  mobileMenu.addEventListener('click', (e) => {
    if (e.target === mobileMenu) toggle(false);
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') toggle(false);
  });
}


/* ══════════════════════════════════════════════════════════════
   THREE.JS — HERO PARTICLE NETWORK
══════════════════════════════════════════════════════════════ */
function initHeroCanvas() {
  if (typeof THREE === 'undefined') return;

  const canvas = document.getElementById('heroCanvas');
  if (!canvas) return;

  // ── Scene Setup ────────────────────────────────────────────
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);

  const scene  = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);
  camera.position.z = 200;

  // ── Resize ─────────────────────────────────────────────────
  const resize = () => {
    const w = canvas.parentElement.offsetWidth;
    const h = canvas.parentElement.offsetHeight;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };

  resize();
  window.addEventListener('resize', resize);

  // ── Particle Data ──────────────────────────────────────────
  const PARTICLE_COUNT  = 150;
  const SPREAD          = 280;
  const CONNECT_DIST    = 65;   // max distance to draw line
  const MOUSE_STRENGTH  = 0.018; // parallax strength

  // Positions array (x, y, z) for each particle
  const positions   = new Float32Array(PARTICLE_COUNT * 3);
  const velocities  = [];
  const basePositions = [];

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const x = (Math.random() - 0.5) * SPREAD;
    const y = (Math.random() - 0.5) * SPREAD;
    const z = (Math.random() - 0.5) * SPREAD * 0.4;

    positions[i * 3]     = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;

    basePositions.push(x, y, z);

    velocities.push(
      (Math.random() - 0.5) * 0.12,
      (Math.random() - 0.5) * 0.12,
      0,
    );
  }

  // ── Particle Geometry & Material ───────────────────────────
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  // Paleta alb/gri — monochrome elegant
  const colors = new Float32Array(PARTICLE_COUNT * 3);
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const t = Math.random();
    if (t < 0.5) {
      // alb pur
      const v = 0.85 + Math.random() * 0.15;
      colors[i * 3] = v; colors[i * 3 + 1] = v; colors[i * 3 + 2] = v;
    } else if (t < 0.8) {
      // gri deschis
      const v = 0.5 + Math.random() * 0.25;
      colors[i * 3] = v; colors[i * 3 + 1] = v; colors[i * 3 + 2] = v;
    } else {
      // gri mediu (mai putine)
      const v = 0.3 + Math.random() * 0.15;
      colors[i * 3] = v; colors[i * 3 + 1] = v; colors[i * 3 + 2] = v;
    }
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  const dotMaterial = new THREE.PointsMaterial({
    size: 2.4,
    vertexColors: true,
    transparent: true,
    opacity: 0.85,
    sizeAttenuation: true,
  });

  const points = new THREE.Points(geometry, dotMaterial);
  scene.add(points);

  // ── Line Geometry ──────────────────────────────────────────
  // We'll update line positions every frame
  const MAX_LINES       = PARTICLE_COUNT * 8;
  const linePositions   = new Float32Array(MAX_LINES * 6); // 2 verts * 3 floats
  const lineColors      = new Float32Array(MAX_LINES * 6);

  const lineGeometry = new THREE.BufferGeometry();
  const linePosAttr  = new THREE.BufferAttribute(linePositions, 3);
  const lineColAttr  = new THREE.BufferAttribute(lineColors, 3);
  linePosAttr.setUsage(THREE.DynamicDrawUsage);
  lineColAttr.setUsage(THREE.DynamicDrawUsage);
  lineGeometry.setAttribute('position', linePosAttr);
  lineGeometry.setAttribute('color',    lineColAttr);

  const lineMaterial = new THREE.LineSegments(
    lineGeometry,
    new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.25,
      blending: THREE.AdditiveBlending,
    })
  );
  scene.add(lineMaterial);

  // ── Mouse Parallax ─────────────────────────────────────────
  const mouse = { x: 0, y: 0 };
  const targetMouse = { x: 0, y: 0 };

  window.addEventListener('mousemove', (e) => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    targetMouse.x = (e.clientX / w - 0.5) * 2;
    targetMouse.y = (e.clientY / h - 0.5) * 2;
  });

  // ── Animation Loop ─────────────────────────────────────────
  let frameId;
  let time = 0;

  const animate = () => {
    frameId = requestAnimationFrame(animate);
    time += 0.005;

    // Smooth mouse lerp
    mouse.x += (targetMouse.x - mouse.x) * 0.04;
    mouse.y += (targetMouse.y - mouse.y) * 0.04;

    const pos = geometry.attributes.position.array;

    // Update particle positions
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const idx = i * 3;

      // Drift movement
      pos[idx]     += velocities[i * 3];
      pos[idx + 1] += velocities[i * 3 + 1];

      // Gentle sine wave overlay
      const wave = Math.sin(time + i * 0.4) * 0.05;
      pos[idx]     += wave;
      pos[idx + 1] += Math.cos(time + i * 0.3) * 0.03;

      // Mouse parallax offset applied on top of base
      pos[idx]     += mouse.x * MOUSE_STRENGTH * (basePositions[idx]     / (SPREAD * 0.5));
      pos[idx + 1] -= mouse.y * MOUSE_STRENGTH * (basePositions[idx + 1] / (SPREAD * 0.5));

      // Wrap-around boundaries
      const half = SPREAD * 0.55;
      if (pos[idx]     > half)  { pos[idx]     = -half; basePositions[idx]     = -half; }
      if (pos[idx]     < -half) { pos[idx]     =  half; basePositions[idx]     =  half; }
      if (pos[idx + 1] > half)  { pos[idx + 1] = -half; basePositions[idx + 1] = -half; }
      if (pos[idx + 1] < -half) { pos[idx + 1] =  half; basePositions[idx + 1] =  half; }
    }

    geometry.attributes.position.needsUpdate = true;

    // Build line segments between nearby particles
    let lineCount = 0;

    for (let i = 0; i < PARTICLE_COUNT && lineCount < MAX_LINES; i++) {
      for (let j = i + 1; j < PARTICLE_COUNT && lineCount < MAX_LINES; j++) {
        const dx = pos[i * 3]     - pos[j * 3];
        const dy = pos[i * 3 + 1] - pos[j * 3 + 1];
        const dz = pos[i * 3 + 2] - pos[j * 3 + 2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (dist < CONNECT_DIST) {
          const alpha = 1 - dist / CONNECT_DIST; // fade with distance

          const base = lineCount * 6;

          // Vertex A
          linePositions[base]     = pos[i * 3];
          linePositions[base + 1] = pos[i * 3 + 1];
          linePositions[base + 2] = pos[i * 3 + 2];

          // Vertex B
          linePositions[base + 3] = pos[j * 3];
          linePositions[base + 4] = pos[j * 3 + 1];
          linePositions[base + 5] = pos[j * 3 + 2];

          // Line color — electric blue with alpha-based brightness
          const c = alpha * 0.8;
          lineColors[base]     = 0;
          lineColors[base + 1] = c * 0.85;
          lineColors[base + 2] = c;
          lineColors[base + 3] = 0;
          lineColors[base + 4] = c * 0.85;
          lineColors[base + 5] = c;

          lineCount++;
        }
      }
    }

    lineGeometry.attributes.position.needsUpdate = true;
    lineGeometry.attributes.color.needsUpdate = true;
    lineGeometry.setDrawRange(0, lineCount * 2);

    renderer.render(scene, camera);
  };

  animate();

  // ── Cleanup on page hide ───────────────────────────────────
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(frameId);
    } else {
      animate();
    }
  });
}


/* ══════════════════════════════════════════════════════════════
   COUNTER ANIMATION — counts up when in viewport
══════════════════════════════════════════════════════════════ */
function initCounters() {
  const counters = document.querySelectorAll('.counter');
  if (!counters.length) return;

  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  const animateCounter = (el, target, duration = 1800) => {
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOut(progress);
      const current = Math.round(eased * target);
      el.textContent = current;

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = target;
      }
    };

    requestAnimationFrame(step);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const target = parseInt(el.dataset.target, 10);
          animateCounter(el, target);
          observer.unobserve(el);
        }
      });
    },
    { threshold: 0.5 }
  );

  counters.forEach((counter) => observer.observe(counter));
}


/* ══════════════════════════════════════════════════════════════
   GSAP + SCROLLTRIGGER
══════════════════════════════════════════════════════════════ */
function initGSAP() {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  // ── Service Cards stagger ──────────────────────────────────
  const cards = gsap.utils.toArray('.service-card');
  if (cards.length) {
    gsap.from(cards, {
      scrollTrigger: {
        trigger: '.services-grid',
        start: 'top 80%',
        once: true,
      },
      y: 48,
      opacity: 0,
      duration: 0.7,
      stagger: 0.12,
      ease: 'power3.out',
    });
  }

  // ── About section text ─────────────────────────────────────
  gsap.from('.about-text', {
    scrollTrigger: {
      trigger: '.about',
      start: 'top 75%',
      once: true,
    },
    x: -40,
    opacity: 0,
    duration: 0.9,
    ease: 'power3.out',
  });

  gsap.from('.about-stats', {
    scrollTrigger: {
      trigger: '.about',
      start: 'top 75%',
      once: true,
    },
    x: 40,
    opacity: 0,
    duration: 0.9,
    delay: 0.15,
    ease: 'power3.out',
  });

  // ── Slide content stagger ──────────────────────────────────
  const slideContents = gsap.utils.toArray('.slide-content');
  slideContents.forEach((content) => {
    gsap.from(content.querySelectorAll('.slide-tag, .slide-title, .slide-desc'), {
      scrollTrigger: {
        trigger: content,
        start: 'top 85%',
        once: true,
      },
      y: 28,
      opacity: 0,
      duration: 0.65,
      stagger: 0.12,
      ease: 'power2.out',
    });
  });

  // ── Career section ─────────────────────────────────────────
  gsap.from('.career-text', {
    scrollTrigger: {
      trigger: '.career',
      start: 'top 75%',
      once: true,
    },
    x: -32,
    opacity: 0,
    duration: 0.8,
    ease: 'power3.out',
  });

  gsap.from('.career-cta', {
    scrollTrigger: {
      trigger: '.career',
      start: 'top 75%',
      once: true,
    },
    x: 32,
    opacity: 0,
    duration: 0.8,
    delay: 0.2,
    ease: 'power3.out',
  });

  // ── Footer brand ──────────────────────────────────────────
  gsap.from('.footer-brand', {
    scrollTrigger: {
      trigger: 'footer',
      start: 'top 90%',
      once: true,
    },
    y: 24,
    opacity: 0,
    duration: 0.7,
    ease: 'power2.out',
  });
}


/* ══════════════════════════════════════════════════════════════
   CONTACT FORM
══════════════════════════════════════════════════════════════ */
function initContactForm() {
  const form    = document.getElementById('contactForm');
  const success = document.getElementById('formSuccess');
  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();

    const name    = form.name.value.trim();
    const email   = form.email.value.trim();
    const message = form.message.value.trim();

    if (!name || !email || !message) {
      [form.name, form.email, form.message].forEach((field) => {
        if (!field.value.trim()) {
          field.style.borderColor = '#ff4d4d';
          field.addEventListener('input', () => {
            field.style.borderColor = '';
          }, { once: true });
        }
      });
      return;
    }

    const btn      = form.querySelector('button[type="submit"]');
    const btnText  = btn.querySelector('.btn-text');
    const origText = btnText.textContent;

    btn.disabled = true;
    btnText.textContent = 'Wird gesendet…';

    const templateParams = {
      from_name:  name,
      from_email: email,
      phone:      form.phone ? form.phone.value.trim() : '',
      service:    form.service ? form.service.value : '',
      message:    message,
    };

    emailjs.send('service_t1oqa3x', 'template_odctew7', templateParams)
      .then(() => {
        form.reset();
        if (success) {
          success.style.display = 'block';
          setTimeout(() => { success.style.display = 'none'; }, 5000);
        }
      })
      .catch(() => {
        alert('Ein Fehler ist aufgetreten. Bitte kontaktieren Sie uns direkt unter kontakt@timmy-t.com');
      })
      .finally(() => {
        btn.disabled = false;
        btnText.textContent = origText;
      });
  });
}
